import classNames from 'classnames';
import { IconButton } from '@hub/common/IconButton/IconButton';
import { IconColor, IconID } from '@hub/common/Icon/Icon';
import { useTranslation } from 'next-i18next';
import { create } from 'zustand';

export enum ToastType {
  SUCCESS = 'bg-igus-green',
  ERROR = 'bg-red-800',
  INFO = 'bg-[#2480a8]',
}

export enum ToastPosition {
  TOP = 'top-72 w-auto left-1/2 transform -translate-x-1/2',
  // Removed Bottom poisition as it would overlay i.e. the chat window on live. Kept position in case we need it later
}

type Toast = {
  id: number;
  content: string | JSX.Element;
  type?: ToastType;
  position?: ToastPosition;
  allowMultiple?: boolean;
};

type ToastStore = {
  toasts: Toast[];
  removeToast: (id: number) => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  reset: () => void;
};

const initialState = {
  toasts: [],
};

export const useToasts = create<ToastStore>((set, get) => ({
  ...initialState,
  removeToast: id => set(state => ({ ...state, toasts: state.toasts.filter(t => t.id !== id) })),
  addToast: toast => {
    let toasts = [...get().toasts];
    const id = toast.allowMultiple ? new Date().getTime() : 1;
    const newToast = {
      ...toast,
      position: toast.position || ToastPosition.TOP,
      id,
    };
    if (toast.allowMultiple) {
      toasts.push(newToast);
    } else {
      toasts = [newToast];
    }
    set(state => ({ ...state, toasts })); // only one toast at a time
    setTimeout(() => {
      get().removeToast(newToast.id);
    }, 5000);
  },
  reset: () => set(initialState),
}));

export const Toasts = () => {
  const toasts = useToasts(state => state.toasts);
  const { removeToast } = useToasts();
  const { t } = useTranslation(['layout/general']);
  if (!toasts.length) {
    return null;
  }
  return (
    // I had to use style prop, because custom z-index doesn't work in Tailwind strangely. it's probably removed by postcss
    <div data-testid="toast-container" className="fixed top-0 left-0" style={{ zIndex: 500 }}>
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={classNames([
            'fixed',
            toast.position,
            toast.type,
            'rounded-md',
            'p-4',
            'mx-2',
            'text-white',
            'flex',
            'justify-between',
          ])}
          data-testid={`toast-${toast.id}`}
        >
          <div className="order-first">
            {typeof toast.content === 'string' ? (
              <p className="font-semibold text-2xl">{toast.content}</p>
            ) : (
              toast.content
            )}
          </div>
          <div className="order-last pt-1 pl-4">
            <IconButton
              size={6}
              icon={IconID.CLOSE_CROSS}
              iconColor={IconColor.WHITE}
              primary={false}
              onClick={() => removeToast(toast.id)}
              ariaLabel={t('GENERAL.CLOSE')}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
